import React from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Tab, Tabs, TextField, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/bg';
import MuscleLoadVisualization from './MuscleLoadVisualization';
import ProgressFilters from './progress/ProgressFilters';
import ProgressStats from './progress/ProgressStats';
import StrengthCharts from './progress/StrengthCharts';
import CardioCharts from './progress/CardioCharts';
import WorkoutHistoryTable from './progress/WorkoutHistoryTable';
import CardioHistory from './progress/CardioHistory';
import ExerciseStatsTable from './progress/ExerciseStatsTable';
import GroupTrendCharts from './progress/GroupTrendCharts';
import GroupDeltasPanel from './progress/GroupDeltasPanel';
import ExerciseGroupManagerDialog from './progress/ExerciseGroupManagerDialog';
import { useProgressData } from './progress/useProgressData';
import ExerciseService from '../services/exerciseService';

const printStyles = `
  .print-only-el { display: none !important; }
  @media print {
    @page {
      size: auto;
      margin: 8mm;
    }
    .print-only-el,
    .print-only-el * {
      print-color-adjust: exact !important;
      -webkit-print-color-adjust: exact !important;
    }
    .no-print { display: none !important; }
    .progress-filters { display: none !important; }
    .print-only-el { display: block !important; }
    .progress-stats {
      display: grid !important;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      gap: 6px !important;
      margin-bottom: 8px !important;
    }
    .progress-stat-card {
      break-inside: avoid;
      page-break-inside: avoid;
      box-shadow: none !important;
    }
    .progress-stat-card-content {
      padding: 7px !important;
    }
    .progress-stat-label {
      font-size: 0.54rem !important;
      letter-spacing: 0.04em !important;
      line-height: 1.1 !important;
    }
    .progress-stat-value {
      font-size: 1rem !important;
      line-height: 1.05 !important;
      margin-top: 2px !important;
    }
    .print-tables {
      display: grid !important;
      grid-template-columns: minmax(250px, 0.82fr) minmax(0, 1.58fr);
      align-items: start;
      gap: 8px !important;
      width: 100%;
    }
    .print-muscle-panel,
    .print-data-panel {
      break-inside: avoid;
      page-break-inside: avoid;
      border: 1px solid #d7dde5 !important;
      border-radius: 10px;
      background: #fff !important;
      box-shadow: none !important;
    }
    .print-muscle-panel {
      padding: 8px !important;
    }
    .print-muscle-map .exercise-muscle-map {
      margin: 0 auto;
      max-width: 310px;
    }
    .print-muscle-map .exercise-muscle-map--print {
      width: 100%;
    }
    .print-tables-data {
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px !important;
      align-items: start;
    }
    .print-data-panel {
      padding: 8px !important;
    }
    .print-data-panel--full {
      grid-column: 1 / -1;
    }
    .print-section-title {
      margin: 0 0 6px 0 !important;
      font-size: 11px !important;
      font-weight: 700 !important;
      color: #0f172a !important;
      letter-spacing: 0.02em;
    }
    .print-muscle-legend,
    .print-data-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 9.5px;
    }
    .print-muscle-legend {
      margin-top: 8px;
      table-layout: fixed;
    }
    .print-muscle-legend tr:nth-of-type(even),
    .print-data-table tbody tr:nth-of-type(even) {
      background: #f8fafc !important;
    }
    .print-muscle-legend td,
    .print-data-table th,
    .print-data-table td {
      border: 1px solid #d4dbe3;
      padding: 4px 5px;
      text-align: center;
    }
    .print-muscle-legend td:first-of-type,
    .print-data-table td:first-of-type {
      text-align: left;
    }
    .print-muscle-legend td:first-of-type {
      font-weight: 600;
      width: 38%;
    }
    .print-data-table th {
      background: #eef4f8 !important;
      font-weight: 700;
      color: #0f172a !important;
    }
    .print-data-table td strong {
      color: #0b3b66 !important;
    }
  }
`;

const Progress: React.FC = () => {

  const {
    progressTab,
    setProgressTab,
    loading,
    viewMode,
    workoutProgress,
    exerciseProgress,
    cardioProgress,
    exerciseOptions,
    selectedExerciseId,
    selectedProgramId,
    selectedProgramDay,
    availableProgramDays,
    selectedVariation,
    selectedMuscleGroup,
    selectedTrainingLabel,
    selectedGymId,
    programs,
    gyms,
    successRates,
    dateFrom,
    dateTo,
    groupedExerciseOptions,
    variationOptions,
    chartData,
    currentWorkouts,
    lastExecution,
    lastSessionDetail,
    programDaysForVisualization,
    workoutSessionsForVisualization,
    printMuscleValues,
    printMuscleWeight,
    exerciseStats,
    exerciseStatsLoading,
    exerciseRecords,
    setExerciseRecords,
    exerciseRecordsLoading,
    exerciseRecordsError,
    groups,
    selectedGroupId,
    groupAnalysis,
    groupAnalysisLoading,
    refreshGroups,
    handleGroupChange,
    calculateImprovement,
    handleProgramChange,
    handleExerciseChange,
    handleProgramDayChange,
    handleVariationChange,
    handleMuscleGroupChange,
    handleTrainingLabelChange,
    handleGymChange,
    handleDateChange,
    refreshSelectedExerciseData,
  } = useProgressData();
  const [groupManagerOpen, setGroupManagerOpen] = React.useState(false);
  const [prDialogOpen, setPrDialogOpen] = React.useState(false);
  const [prValue, setPrValue] = React.useState('');
  const [prRecordedAt, setPrRecordedAt] = React.useState(() => new Date().toISOString().split('T')[0]);
  const [prNote, setPrNote] = React.useState('');
  const [prSaveError, setPrSaveError] = React.useState<string | null>(null);
  const [prSaveSuccess, setPrSaveSuccess] = React.useState<string | null>(null);
  const [prSaving, setPrSaving] = React.useState(false);

  const hasPrintableData = progressTab === 'cardio'
    ? cardioProgress.length > 0
    : currentWorkouts.length > 0;

  const handlePrint = React.useCallback(() => {
    if (!hasPrintableData) return;
    if (typeof window !== 'undefined') {
      if (Array.isArray((window as any).dataLayer)) {
        (window as any).dataLayer.push({ event: 'progress_print', timestamp: new Date().toISOString() });
      } else if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', 'progress_print', { timestamp: new Date().toISOString() });
      }
    }
    window.print();
  }, [hasPrintableData]);

  const formatMetric = (value: number) => value.toLocaleString('bg-BG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatSessionDate = (value: string | null) => (value ? new Date(value).toLocaleDateString('bg-BG') : '-');
  const formatRecordValue = (value: number | null | undefined) => value === null || value === undefined
    ? 'Недостатъчно данни'
    : `${formatMetric(value)} kg`;

  const openPrDialog = React.useCallback(() => {
    setPrValue(exerciseRecords?.current.pr?.value ? String(exerciseRecords.current.pr.value) : '');
    setPrRecordedAt(exerciseRecords?.current.pr?.recordedAt?.slice(0, 10) ?? new Date().toISOString().split('T')[0]);
    setPrNote(exerciseRecords?.current.pr?.note ?? '');
    setPrSaveError(null);
    setPrDialogOpen(true);
  }, [exerciseRecords]);

  const handlePrSave = React.useCallback(async () => {
    if (!selectedExerciseId) {
      return;
    }

    setPrSaving(true);
    setPrSaveError(null);

    try {
      const updated = await ExerciseService.updateExercisePr(selectedExerciseId, {
        value: Number(prValue),
        recorded_at: prRecordedAt || undefined,
        note: prNote.trim() || undefined,
      });
      setExerciseRecords(updated);
      setPrDialogOpen(false);
      setPrSaveSuccess('PR е обновен.');
      void refreshSelectedExerciseData();
    } catch (error) {
      const parsed = ExerciseService.parseApiError(error);
      if (parsed.status === 422 && parsed.body?.errors) {
        const firstField = Object.values(parsed.body.errors)[0];
        setPrSaveError(Array.isArray(firstField) ? String(firstField[0]) : parsed.message);
      } else if (parsed.status === 401) {
        setPrSaveError('Трябва да влезеш в профила си отново.');
      } else if (parsed.status === 403) {
        setPrSaveError('Нямаш достъп да редактираш този рекорд.');
      } else if (parsed.status === 404) {
        setPrSaveError('Упражнението не беше намерено.');
      } else {
        setPrSaveError('Неуспешно запазване на PR. Опитай отново.');
      }
    } finally {
      setPrSaving(false);
    }
  }, [prNote, prRecordedAt, prValue, refreshSelectedExerciseData, selectedExerciseId, setExerciseRecords]);

  return (
    <Box>
      <style>{printStyles}</style>

      <Box className="no-print" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Tabs value={progressTab} onChange={(_, v) => setProgressTab(v)}>
          <Tab value="strength" label="Силов Прогрес" />
          <Tab value="cardio" label="Кардио" />
        </Tabs>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          <Button
            onClick={handlePrint}
            variant="outlined"
            startIcon={<PrintIcon />}
            size="small"
            disabled={!hasPrintableData}
            data-testid="progress-print-button"
          >
            Print
          </Button>
          {!hasPrintableData && (
            <Typography variant="body2" color="text.secondary">Няма данни за принтиране</Typography>
          )}
        </Box>
      </Box>

      {progressTab === 'strength' && (
        <Box>
          <ProgressFilters
            selectedProgramId={selectedProgramId}
            selectedExerciseId={selectedExerciseId}
            selectedVariation={selectedVariation}
            selectedMuscleGroup={selectedMuscleGroup}
            selectedTrainingLabel={selectedTrainingLabel}
            selectedProgramDay={selectedProgramDay}
            selectedGymId={selectedGymId}
            programs={programs}
            gyms={gyms}
            availableProgramDays={availableProgramDays}
            dateFrom={dateFrom}
            dateTo={dateTo}
            groups={groups}
            selectedGroupId={viewMode === 'group' ? selectedGroupId : null}
            groupedExerciseOptions={groupedExerciseOptions}
            variationOptions={variationOptions}
            onGroupChange={handleGroupChange}
            onManageGroups={() => setGroupManagerOpen(true)}
            onProgramChange={handleProgramChange}
            onExerciseChange={handleExerciseChange}
            onVariationChange={handleVariationChange}
            onMuscleGroupChange={handleMuscleGroupChange}
            onTrainingLabelChange={handleTrainingLabelChange}
            onProgramDayChange={handleProgramDayChange}
            onGymChange={handleGymChange}
            onDateChange={handleDateChange}
          />

          {viewMode === 'group' ? (
            groupAnalysisLoading ? (
              <Typography sx={{ p: 2 }}>Зареждане...</Typography>
            ) : !groupAnalysis || (groupAnalysis.stats.length === 0 && groupAnalysis.perExerciseSeries.length === 0) ? (
              <Typography sx={{ p: 2 }}>Няма данни за групата в избрания период.</Typography>
            ) : (
              <>
                <ExerciseStatsTable rows={groupAnalysis.stats} />
                <GroupTrendCharts series={groupAnalysis.perExerciseSeries} />
                <GroupDeltasPanel deltas={groupAnalysis.deltas} />
              </>
            )
          ) : loading ? (
            <Typography sx={{ p: 2 }}>Зареждане...</Typography>
          ) : currentWorkouts.length === 0 ? (
            <Typography sx={{ p: 2 }}>Няма данни за избраните филтри.</Typography>
          ) : (
            <>
              {viewMode === 'exercise' && selectedExerciseId !== null && (
                <Paper className="no-print" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Лични рекорди
                    </Typography>
                    <Button variant="outlined" size="small" onClick={openPrDialog}>
                      Редактирай PR
                    </Button>
                  </Box>

                  {exerciseRecordsError && <Alert severity="error" sx={{ mb: 1.5 }}>{exerciseRecordsError}</Alert>}

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Текущ e1RM</Typography>
                      <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700 }}>
                        {exerciseRecordsLoading ? 'Зареждане...' : formatRecordValue(exerciseRecords?.current.e1rm?.value ?? null)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {exerciseRecords?.current.e1rm ? 'Изчислен от серии' : 'Недостатъчно данни'}
                      </Typography>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Текущ PR</Typography>
                      <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700 }}>
                        {exerciseRecordsLoading ? 'Зареждане...' : formatRecordValue(exerciseRecords?.current.pr?.value ?? null)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {exerciseRecords?.current.pr?.source === 'manual' ? 'Ръчно въведен' : exerciseRecords?.current.pr ? 'Изчислен от серии' : 'Недостатъчно данни'}
                      </Typography>
                    </Paper>
                  </Box>
                </Paper>
              )}

              <ProgressStats
                chartData={chartData}
                successRates={successRates}
                calculateImprovement={calculateImprovement}
              />

              {Object.keys(programDaysForVisualization).length > 0 && (
                <Box className="no-print" sx={{ mb: 2 }}>
                  <MuscleLoadVisualization
                    programDays={programDaysForVisualization}
                    workoutSessions={workoutSessionsForVisualization}
                    exerciseOptions={exerciseOptions}
                  />
                </Box>
              )}

              <StrengthCharts
                chartData={chartData}
                lastExecution={lastExecution}
                lastSessionDetail={lastSessionDetail}
                printMuscleValues={printMuscleValues}
                printMuscleWeight={printMuscleWeight}
              />

              <ExerciseStatsTable rows={exerciseStats} loading={exerciseStatsLoading} />

              <WorkoutHistoryTable
                viewMode={viewMode}
                workoutProgress={workoutProgress}
                exerciseProgress={exerciseProgress}
                successRates={successRates}
              />
            </>
          )}
        </Box>
      )}

      {progressTab === 'cardio' && (
        <Box>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="bg">
            <Box className="no-print" sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <DatePicker
                label="От"
                value={dayjs(dateFrom)}
                format="DD.MM.YYYY"
                onChange={(val: Dayjs | null) => {
                  if (val?.isValid()) {
                    handleDateChange(val.format('YYYY-MM-DD'), dateTo);
                  }
                }}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 140, maxWidth: 160 } } }}
              />
              <DatePicker
                label="До"
                value={dayjs(dateTo)}
                format="DD.MM.YYYY"
                onChange={(val: Dayjs | null) => {
                  if (val?.isValid()) {
                    handleDateChange(dateFrom, val.format('YYYY-MM-DD'));
                  }
                }}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 140, maxWidth: 160 } } }}
              />
            </Box>
          </LocalizationProvider>

          {loading ? (
            <Typography sx={{ p: 2 }}>Зареждане...</Typography>
          ) : cardioProgress.length === 0 ? (
            <Typography sx={{ p: 2 }}>Няма налични кардио сесии.</Typography>
          ) : (
            <>
              <CardioCharts cardioProgress={cardioProgress} />
              <CardioHistory cardioProgress={cardioProgress} />
            </>
          )}
        </Box>
      )}

      <Dialog open={prDialogOpen} onClose={() => !prSaving && setPrDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Редактирай PR</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 1.5, pt: 1 }}>
            {prSaveError && <Alert severity="error">{prSaveError}</Alert>}
            <TextField
              label="Стойност (kg)"
              value={prValue}
              onChange={(event) => setPrValue(event.target.value)}
              type="number"
              inputProps={{ min: 0, max: 2000, step: 0.01 }}
              fullWidth
            />
            <TextField
              label="Дата"
              type="date"
              value={prRecordedAt}
              onChange={(event) => setPrRecordedAt(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Бележка"
              value={prNote}
              onChange={(event) => setPrNote(event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrDialogOpen(false)} disabled={prSaving}>Отказ</Button>
          <Button onClick={handlePrSave} variant="contained" disabled={prSaving || prValue.trim() === ''}>
            {prSaving ? 'Запазване...' : 'Запази'}
          </Button>
        </DialogActions>
      </Dialog>

      {prSaveSuccess && (
        <Alert severity="success" sx={{ position: 'fixed', right: 16, bottom: 16, zIndex: 2000 }} onClose={() => setPrSaveSuccess(null)}>
          {prSaveSuccess}
        </Alert>
      )}

      <ExerciseGroupManagerDialog
        open={groupManagerOpen}
        onClose={() => setGroupManagerOpen(false)}
        groups={groups}
        onChanged={() => { void refreshGroups(); }}
      />
    </Box>
  );
};

export default Progress;
